/**
 * This app's overlay switchboard — the tenancy POLICY, as the dashboard sets it.
 *
 * The vocabulary and the resolution rules live in
 * `@authe/fe-commons/attribution` (`scope.ts`), because the admin portal shows
 * the same tenancy and a second copy of "what does private mean" is a second
 * answer. What is left here is the part that is genuinely a product decision for
 * THIS app: which rungs each resource offers, what a new attach defaults to, and
 * which tier a resolved view opens at.
 *
 * To light up org / customer overlays, or to make new attaches private by
 * default, change it here (or in the shared phase-1 baseline, for every app at
 * once) — no component changes. To ship a resource dark, `enabled: false`.
 */
import {
  attributionPolicies,
  policyFor,
  resolveReadScope,
  resolveWriteScope,
  type AttributionPolicies,
  type AttributionReadScope,
  type AttributionResource,
  type AttributionResourcePolicy,
  type AttributionScope,
  type ReadScopeOverrides,
  type WriteScopeOverrides,
} from "@authe/fe-commons/attribution";

export type {
  AttributionPolicies,
  AttributionReadScope,
  AttributionResource,
  AttributionResourcePolicy,
  AttributionScope,
  ReadScopeOverrides,
  WriteScopeOverrides,
};

/** Personal protocol settings are isolated to user-body pipeline runs. */
export const OVERLAY_CONFIG: AttributionPolicies = attributionPolicies({
  protocol: { writeScopes: ["private", "tenant", "org", "customer"] },
});

/** The policy for `resource` under this app's table (never throws). */
export function overlayConfig(resource: AttributionResource): AttributionResourcePolicy {
  return policyFor(resource, OVERLAY_CONFIG);
}

/** Convenience: is the overlay surface on for this resource at all? */
export function isOverlayEnabled(resource: AttributionResource): boolean {
  return overlayConfig(resource).enabled;
}

/** The effective default write scope for `resource` under this app's table. */
export function dashboardWriteScope(
  overrides: WriteScopeOverrides | null | undefined,
  resource: AttributionResource,
): AttributionScope {
  return resolveWriteScope(overrides, resource, OVERLAY_CONFIG);
}

/** The read-axis mirror of {@link dashboardWriteScope}. */
export function dashboardReadScope(
  overrides: ReadScopeOverrides | null | undefined,
  resource: AttributionResource,
): AttributionReadScope {
  return resolveReadScope(overrides, resource, OVERLAY_CONFIG);
}
